import { Form, Formik } from "formik";
import React from "react";
import styled, { css } from "styled-components";
import { Button } from "./Button";
import { TextInput } from "./TextInput";
import { InputWrapper } from "./TextInputUtils";
import { LoadingState } from "./states/LoadingState";
import { style } from "./style";

export type Props = PaginatorClickEventProps & {
  className?: string;
  pagination: PaginationProps;
  inline?: boolean;
  disabled?: boolean;
  loading?: boolean;
};

type ContainerProps = {
  disabled?: boolean;
};

export type PaginationProps = {
  page: number;
  pages: number;
  per_page: number;
  total?: number;
  sort_direction?: "asc" | "desc";
  sort?: string;
};

export type PaginatorClickEventProps = {
  fetchMore?: ({
    variables,
    updateQuery,
  }: {
    variables: {
      pagination: {
        per_page: number;
        page: number;
        sort_direction?: "asc" | "desc";
        sort?: string;
      };
    };
    updateQuery: (prevPageResult: any, { fetchMoreResult }: any) => any;
  }) => any;
  onPrevious?: () => any;
  onNext?: () => any;
  onSubmit?: (pageFromInput: number) => any;
};

const Container = styled.div<ContainerProps>`
  ${({ disabled }) =>
    disabled &&
    css`
      opacity: ${style.vars.disabledOpacity};
      cursor: not-allowed;
    `}
`;

const Label = styled.span`
  font-weight: 600;
  margin: 0 ${style.vars.space["1"]} 0 ${style.vars.space["2"]};
`;

const InlineLabel = styled(Label)`
  margin-right: ${style.vars.space["2"]};
`;

const LoadingIcon = styled(LoadingState)`
  width: 18px;
  height: 18px;
  vertical-align: sub;
  margin: 0 ${style.vars.space["2"]};
`;

const InlineLoadingIcon = styled(LoadingIcon)`
  /* Align icon so it doesn't move slightly when changing to and from loading state */
  margin-right: ${style.vars.space["2"]};
  margin-left: -3px;
`;

const PaginatorForm = styled(Form)`
  display: inline-block;
  margin: 0 ${style.vars.space["3"]};

  ${InputWrapper} {
    min-width: 0;
  }
`;

const Input = styled(TextInput)`
  && input {
    min-width: initial;
    max-width: 40px;
    height: 40px;
    padding: 0;
    text-align: center;
  }
`;

const TotalPageText = styled.span`
  display: inline-block;
  min-width: 18px;
  margin: 0 ${style.vars.space["2"]};
  text-align: center;
`;

const InlineButtonLeft = styled(Button)`
  margin-right: ${style.vars.space["1"]};
`;

export const onUpdatedQuery = <T,>(prevPageResult: T, { fetchMoreResult }: { fetchMoreResult: T }): T => {
  let updatedResult;

  if (fetchMoreResult) {
    updatedResult = fetchMoreResult;
  } else {
    updatedResult = prevPageResult;
  }

  return updatedResult;
};

const InlinePaginator: React.FC<Props> = ({ pagination, disabled, loading, fetchMore, onPrevious, onNext }) => {
  const { page, pages, per_page, sort_direction, sort } = pagination;
  const startOfRange = (page - 1) * per_page + 1;
  const endOfRange = page * per_page;

  const total = pagination.total ? pagination.total : pages * per_page;
  return (
    <>
      <InlineLabel>
        {startOfRange}-{endOfRange > total ? total : endOfRange} of
        {!loading && ` ${total}`}
      </InlineLabel>

      {loading && <InlineLoadingIcon />}
      <InlineButtonLeft
        aria-label={page === 1 ? "Disabled previous page button" : `Go to page ${page - 1}`}
        variation="icon"
        icon="arrow-left"
        disabled={page === 1 || disabled}
        onClick={() => {
          if (fetchMore) {
            fetchMore({
              variables: {
                pagination: {
                  page: page - 1,
                  per_page,
                  sort_direction,
                  sort,
                },
              },
              updateQuery: onUpdatedQuery,
            });
          } else {
            onPrevious?.();
          }
        }}
      />
      <Button
        aria-label={page === pages ? "Disabled next page button" : `Go to page ${page + 1}`}
        variation="icon"
        icon="arrow-right"
        disabled={page === pages || disabled}
        onClick={() => {
          if (fetchMore) {
            fetchMore({
              variables: {
                pagination: {
                  page: page + 1,
                  per_page,
                  sort_direction,
                  sort,
                },
              },
              updateQuery: onUpdatedQuery,
            });
          } else {
            onNext?.();
          }
        }}
      />
    </>
  );
};

const DefaultPaginator: React.FC<Props> = ({ pagination, disabled, loading, fetchMore, onPrevious, onNext, onSubmit }) => {
  const { page, pages, per_page, sort_direction, sort } = pagination;

  return (
    <>
      <Button
        aria-label={page === 1 ? "Disabled previous page button" : `Go to page ${page - 1}`}
        variation="icon"
        icon="arrow-left"
        disabled={page === 1 || disabled}
        onClick={() => {
          if (fetchMore) {
            fetchMore({
              variables: {
                pagination: {
                  page: page - 1,
                  per_page,
                  sort_direction,
                  sort,
                },
              },
              updateQuery: onUpdatedQuery,
            });
          } else {
            onPrevious?.();
          }
        }}
      />

      <Label>
        Page
        <Formik
          enableReinitialize
          initialValues={{
            page,
          }}
          onSubmit={(values) => {
            let currentPage;

            if (values.page < 1) {
              currentPage = 1;
            } else if (values.page > pages) {
              currentPage = pages;
            } else {
              currentPage = values.page;
            }

            if (fetchMore) {
              fetchMore({
                variables: {
                  pagination: {
                    page: Number(currentPage),
                    per_page,
                    sort_direction,
                    sort,
                  },
                },
                updateQuery: onUpdatedQuery,
              });
            } else {
              onSubmit?.(Number(currentPage));
            }
          }}
        >
          {({ values, touched, errors }) => (
            <PaginatorForm>
              <Input
                aria-label={"Current page"}
                name="page"
                type="text"
                disabled={disabled}
                value={values.page}
                touched={touched.page}
                error={errors.page}
              />
            </PaginatorForm>
          )}
        </Formik>
        of
        {loading ? <LoadingIcon /> : <TotalPageText>{pages}</TotalPageText>}
      </Label>
      <Button
        aria-label={page === pages ? "Disabled next page button" : `Go to page ${page + 1}`}
        variation="icon"
        icon="arrow-right"
        disabled={page === pages || disabled}
        onClick={() => {
          if (fetchMore) {
            fetchMore({
              variables: {
                pagination: {
                  page: page + 1,
                  per_page,
                  sort_direction,
                  sort,
                },
              },
              updateQuery: onUpdatedQuery,
            });
          } else {
            onNext?.();
          }
        }}
      />
    </>
  );
};

export const Paginator: React.FC<Props> = ({ inline, className, disabled, ...props }) => {
  const CurrentPaginator = inline ? <InlinePaginator {...props} /> : <DefaultPaginator {...props} />;

  return (
    <Container className={className} disabled={disabled}>
      {CurrentPaginator}
    </Container>
  );
};
